import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ChevronLeft, Download, Trash2, Loader2, AlertTriangle, BookOpen, Pencil, X,
} from 'lucide-react';
import { directDraftsApi } from '../services/api';
import { useDraftsStore } from '../store/draftsStore';
import { useJobsStore } from '../store/jobsStore';
import { Draft, DRAFT_TYPE_LABELS } from '../types';
import { Badge, SkeletonCard } from '../components/ui';

/**
 * The canonical draft viewer (spec §4.4) — serves BOTH kinds. Memo-linked
 * drafts carry a "Backed by memo" chip to the source research; standard
 * drafts are labelled plainly.
 */
export function DraftDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { types, fetchTypes, removeDraft } = useDraftsStore();
  const jobs = useJobsStore((s) => s.jobs);
  const fetchJobs = useJobsStore((s) => s.fetchJobs);

  // Seed instantly from router state (arriving from NewDraftPage), then hydrate.
  const [draft, setDraft] = useState<Draft | null>(
    (location.state as { draft?: Draft } | null)?.draft ?? null,
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');
  // On-platform editing of the generated text (markdown, saved via PATCH).
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchTypes();
    if (!id) return;
    if (draft?.id === id && draft.content_md) return;
    directDraftsApi
      .get(id)
      .then(({ data }) => setDraft(data))
      .catch((err) =>
        setError(err.response?.data?.detail || 'Draft not found'),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Resolve the source memo's title for the "Backed by memo" chip from the
  // slim jobs cache (no full-memo fetch just for a label).
  useEffect(() => {
    if (draft?.job_id && !jobs.some((j) => j.id === draft.job_id)) fetchJobs(100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.job_id]);
  const sourceMemo = draft?.job_id ? jobs.find((j) => j.id === draft.job_id) : undefined;

  const label = draft
    ? types.find((t) => t.type === draft.draft_type)?.label ??
      DRAFT_TYPE_LABELS[draft.draft_type] ??
      draft.draft_type
    : '';

  const handleDownload = async () => {
    if (!draft) return;
    setIsDownloading(true);
    try {
      const response = await directDraftsApi.downloadDocx(draft.id);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${draft.title.replace(/\s+/g, '-').toLowerCase()}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  };

  const startEditing = () => {
    setEditText(draft?.content_md ?? '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!draft || !editText.trim()) return;
    setIsSaving(true);
    setError('');
    try {
      const { data } = await directDraftsApi.update(draft.id, editText);
      setDraft(data);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not save your edit. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft || !confirm('Delete this draft?')) return;
    await directDraftsApi.remove(draft.id);
    removeDraft(draft.id);
    navigate('/app/drafts');
  };

  const handleDeepen = () => {
    if (!draft) return;
    const prefill =
      `I need full legal research behind a ${label} I'm preparing. ` +
      `Context: ${draft.title}. Verify the governing law and case law that support it.`;
    navigate('/app/research/new', {
      state: { prefill, matterId: draft.matter_id ?? undefined },
    });
  };

  if (error) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-legal-200 p-10 text-center text-legal-500 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <SkeletonCard />
      </div>
    );
  }

  const warnings = draft.warnings ?? [];
  // Insert sets created_at == updated_at (same func.now()); any manual save
  // bumps updated_at via onupdate — a few seconds' skew means "edited".
  const wasEdited =
    !!draft.updated_at &&
    new Date(draft.updated_at).getTime() - new Date(draft.created_at).getTime() > 5_000;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/app/drafts')}
        className="flex items-center space-x-1.5 text-sm text-legal-500 hover:text-legal-700 mb-4 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        <span>All drafts</span>
      </button>

      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-medium text-legal-900 truncate">
            {draft.title}
          </h1>
          <p className="text-sm text-legal-400 mt-0.5">
            {label}
            {wasEdited && <span className="ml-2 text-legal-400">· edited on Urava</span>}
          </p>
          {/* Grounding, in plain words (spec §4.4) */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {draft.job_id ? (
              <>
                <Badge tone="info">Cited from your memo</Badge>
                <Link
                  to={`/app/research/${draft.job_id}`}
                  data-track="draft-backed-by-memo"
                  className="inline-flex items-center gap-1.5 text-xs text-legal-600 bg-legal-50 border border-legal-200 rounded-full px-2.5 py-1 hover:border-accent hover:text-accent-700 transition-colors max-w-[20rem]"
                >
                  <BookOpen className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">
                    Backed by memo: {sourceMemo?.title ?? 'view the research'}
                  </span>
                </Link>
              </>
            ) : (
              <Badge tone="neutral">Standard draft</Badge>
            )}
          </div>
          <p className="text-[11px] text-legal-400 mt-1.5">
            Memo-based drafts cite the authorities your research verified · Standard drafts
            cite nothing, so nothing can be wrong.
          </p>
        </div>
        <div className="flex items-center space-x-2 shrink-0">
          {!isEditing && (
            <button
              onClick={startEditing}
              className="flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-legal-300 text-legal-700 rounded-md text-sm font-medium hover:border-accent hover:text-accent-700 transition-colors"
            >
              <Pencil className="h-4 w-4" />
              <span>Edit</span>
            </button>
          )}
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-accent text-legal-900 rounded-md text-sm font-medium hover:bg-accent-400 transition-colors disabled:opacity-60"
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span>Word</span>
          </button>
          <button
            onClick={handleDelete}
            className="p-2 text-legal-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
            title="Delete draft"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-2">
            <AlertTriangle className="h-4 w-4" />
            Check before you rely on this draft
          </p>
          {wasEdited && (
            <p className="text-xs text-amber-600 mb-2 pl-6">
              These checks ran when the draft was generated — re-verify anything your edits changed.
            </p>
          )}
          <ul className="space-y-1.5">
            {warnings.map((w) => (
              <li key={w.id} className="text-sm text-amber-700 pl-6">
                {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isEditing ? (
        <div className="bg-white rounded-2xl border border-accent/40">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            spellCheck={false}
            aria-label="Edit draft text"
            className="w-full min-h-[60vh] p-6 font-serif text-[15px] leading-relaxed text-legal-800 bg-transparent rounded-t-2xl resize-y focus:outline-none"
          />
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-legal-100">
            <p className="text-xs text-legal-400">
              Plain text with markdown headings — saved edits flow into the Word download too.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="flex items-center space-x-1.5 px-3 py-1.5 border border-legal-300 text-legal-600 rounded-md text-sm hover:border-legal-400 transition-colors disabled:opacity-60"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !editText.trim()}
                className="flex items-center space-x-1.5 px-4 py-1.5 bg-accent text-legal-900 rounded-md text-sm font-semibold hover:bg-accent-400 transition-colors disabled:opacity-60"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
                <span>Save changes</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-legal-200">
          <div className="p-6 overflow-y-auto">
            <div className="prose prose-slate max-w-none prose-headings:text-legal-900 prose-p:text-legal-700 font-serif">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.content_md || ''}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {draft.job_id ? (
        <div className="mt-4 p-4 bg-legal-50 border border-legal-200 rounded-xl flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 text-sm text-legal-600 min-w-0">
            <BookOpen className="h-4 w-4 text-accent-600 shrink-0" />
            <span>
              This draft cites the authorities verified in your research memo.
            </span>
          </div>
          <Link
            to={`/app/research/${draft.job_id}`}
            data-track="draft-open-source-memo"
            className="px-4 py-2 bg-white border border-legal-300 text-legal-800 rounded-lg text-sm font-medium hover:border-accent hover:text-accent-700 transition-colors shrink-0"
          >
            Open the memo →
          </Link>
        </div>
      ) : (
        <div className="mt-4 p-4 bg-legal-50 border border-legal-200 rounded-xl flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 text-sm text-legal-600 min-w-0">
            <BookOpen className="h-4 w-4 text-accent-600 shrink-0" />
            <span>
              Need the case law behind this? Run full research and get a memo with
              verified citations.
            </span>
          </div>
          <button
            onClick={handleDeepen}
            className="px-4 py-2 bg-white border border-legal-300 text-legal-800 rounded-lg text-sm font-medium hover:border-accent hover:text-accent-700 transition-colors shrink-0"
          >
            Deepen into research →
          </button>
        </div>
      )}
    </div>
  );
}
